import React from 'react';
import styled, { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslations } from './../../../../../../graphql/components/TranslationsProvider';

import {
  VerticalSpacing,
  Box,
  Text,
  Media,
  Row,
  Col,
} from '../../../../../../components/Boneyard';
import {
  OTP_POPOVER_CONTENT_HEADING,
  OTP_ON_TIME_ARRIVAL_DATA,
  OTP_ON_TIME_ARRIVAL_LABEL,
  OTP_THIRTY_MINUTE_LATE_DATA,
  OTP_THIRTY_MINUTE_LATE_LABEL,
  OTP_CANCELLATIONS_DATA,
  OTP_CANCELLATIONS_LABEL,
  OTP_DISCLAIMER,
  OTP_NO_DATA_MESSAGE,
} from '../../../../../../../automationHooks.json';

const NoOTPDataAvailable = withTheme(({ theme, otpNoDataMessage }) => {
  return (
    <Media>
      {mq => (
        <p style={{ textAlign: 'center' }}>
          <Text
            color={theme.colors.popover.text}
            size={mq.gte('sm') ? 16 : 12}
            weight={500}
            automationHook={OTP_NO_DATA_MESSAGE.hook}
          >
            {otpNoDataMessage}
          </Text>
        </p>
      )}
    </Media>
  );
});

const OTPData = withTheme(
  ({
    theme,
    otpOnTimeArrivalLabel,
    otpThirtyMinuteLateLabel,
    otpCancellationsLabel,
    flightOption,
  }) => {
    return (
      <Media>
        {mq => (
          <>
            <Row type="flex" justify="center" gutter={20}>
              <Col
                xs={flightOption.onTimePerformance.cancellations > 5 ? 24 : 12}
                md={flightOption.onTimePerformance.cancellations > 5 ? 8 : 12}
                style={{ textAlign: 'center' }}
              >
                <Text
                  color={theme.colors.popover.text}
                  size={16}
                  weight={500}
                  automationHook={OTP_ON_TIME_ARRIVAL_LABEL.hook}
                >
                  {otpOnTimeArrivalLabel}
                </Text>
                <VerticalSpacing />
                <Text
                  color={theme.colors.popover.text}
                  size={24}
                  weight={600}
                  automationHook={OTP_ON_TIME_ARRIVAL_DATA.hook}
                >
                  {flightOption.onTimePerformance.onTimeArrival}%
                </Text>
                {mq.lte('sm') &&
                  flightOption.onTimePerformance.cancellations > 5 && (
                    <VerticalSpacing size={6} />
                  )}
              </Col>
              <Col
                xs={flightOption.onTimePerformance.cancellations > 5 ? 24 : 12}
                md={flightOption.onTimePerformance.cancellations > 5 ? 8 : 12}
                style={{ textAlign: 'center' }}
              >
                <Text
                  color={theme.colors.popover.text}
                  size={16}
                  weight={500}
                  automationHook={OTP_THIRTY_MINUTE_LATE_LABEL.hook}
                >
                  {otpThirtyMinuteLateLabel}
                </Text>
                <VerticalSpacing />
                <Text
                  color={theme.colors.popover.text}
                  size={24}
                  weight={
                    flightOption.onTimePerformance.thirtyMinuteLate > 50
                      ? 600
                      : 400
                  }
                  automationHook={OTP_THIRTY_MINUTE_LATE_DATA.hook}
                >
                  {flightOption.onTimePerformance.thirtyMinuteLate}%
                </Text>
                {mq.lte('sm') &&
                  flightOption.onTimePerformance.cancellations > 5 && (
                    <VerticalSpacing size={6} />
                  )}
              </Col>
              {flightOption.onTimePerformance.cancellations > 5 && (
                <Col xs={24} md={8} style={{ textAlign: 'center' }}>
                  <Text
                    color={theme.colors.popover.text}
                    size={16}
                    weight={500}
                    automationHook={OTP_CANCELLATIONS_LABEL.hook}
                  >
                    {otpCancellationsLabel}
                  </Text>
                  <VerticalSpacing />
                  <Text
                    color={theme.colors.popover.text}
                    size={24}
                    weight={600}
                    automationHook={OTP_CANCELLATIONS_DATA.hook}
                  >
                    {flightOption.onTimePerformance.cancellations}%
                  </Text>
                </Col>
              )}
            </Row>
            <VerticalSpacing size={4} />

            <p style={{ textAlign: 'center' }}>
              <small>
                <Text
                  color={theme.colors.popover.text}
                  size={12}
                  weight={500}
                  automationHook={OTP_DISCLAIMER.hook}
                >
                  {flightOption.onTimePerformance.disclaimer}
                </Text>
              </small>
            </p>
          </>
        )}
      </Media>
    );
  }
);

const PopoverContent = withTheme(styled(({ flightOption, theme, ...props }) => {
  const translations = useTranslations();
  const {
    otpContentHeading,
    otpOnTimeArrivalLabel,
    otpThirtyMinuteLateLabel,
    otpCancellationsLabel,
    otpNoDataMessage,
  } = translations.otpPopover;

  return (
    <Media>
      {mq => (
        <div className={props.className}>
          <Box
            p={mq.gte('sm') ? 6 : 4}
            minWidth={mq.gte('sm') ? 500 : undefined}
            maxWidth={800}
          >
            <Box bb="1px dashed" pb={2} pt={mq.gte('sm') ? 0 : 2}>
              <h1 style={{ textAlign: mq.gte('sm') ? 'left' : 'center' }}>
                <Text
                  color={theme.colors.popover.text}
                  size={mq.gte('sm') ? 22 : 18}
                  weight={600}
                  automationHook={OTP_POPOVER_CONTENT_HEADING.hook}
                >
                  {otpContentHeading}
                </Text>
              </h1>
            </Box>
            <VerticalSpacing size={4} />
            {!flightOption.onTimePerformance ? (
              <NoOTPDataAvailable otpNoDataMessage={otpNoDataMessage} />
            ) : (
              <OTPData
                otpOnTimeArrivalLabel={otpOnTimeArrivalLabel}
                otpThirtyMinuteLateLabel={otpThirtyMinuteLateLabel}
                otpCancellationsLabel={otpCancellationsLabel}
                flightOption={flightOption}
              />
            )}
          </Box>
        </div>
      )}
    </Media>
  );
})`
  cursor: auto;
`);

PopoverContent.propTypes = {
  theme: PropTypes.object,
  flightOption: PropTypes.object.isRequired,
};

export default PopoverContent;
